/**
 * Ampel-Aggregation fuer die Timeline-Balken.
 *
 * Bildet aus den 17 Schritt-Reifegraden (0-5) eines Projekts die Verteilung
 * der Ampel-Status (done/wip/open/idle). Das Reifegrad->Ampel-Mapping und das
 * Phase-Gating sind IDENTISCH zur DashboardTab-Logik (single source of truth
 * dort gespiegelt): eine Phase ist aktiv, sobald die Vorphase im Schnitt
 * Reifegrad >= 3 erreicht ODER in der Phase selbst schon gearbeitet wurde.
 *
 * - idle:  Phase noch nicht erreicht (neutraler Rest)
 * - open:  aktive Phase, Reifegrad 0-1 (rot)
 * - wip:   Reifegrad 2-3 (gelb)
 * - done:  Reifegrad >= 4 (gruen)
 */

export interface AmpelBuckets {
  open: number;
  wip: number;
  done: number;
  idle: number;
  total: number;
}

const INIT = [1, 2, 3, 4, 5, 6, 7];
const PLAN = [8, 9, 10, 11, 12, 13];
const EXEC = [14, 15, 16, 17];

function avg(a: number[]): number {
  return a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0;
}

function touched(a: number[]): boolean {
  return a.some((l) => l > 0);
}

/**
 * Zaehlt die Ampel-Buckets ueber alle 17 Schritte.
 * @param steps Array der Laenge 17 — Index 0 = Schritt 1 ... Index 16 = Schritt 17,
 *              Wert = Reifegrad 0-5 (fehlend/kuerzer => 0).
 */
export function computeAmpelBuckets(steps: number[]): AmpelBuckets {
  const lvl = (n: number) => steps[n - 1] ?? 0;
  const initLevels = INIT.map(lvl);
  const planLevels = PLAN.map(lvl);
  const execLevels = EXEC.map(lvl);

  const planActive = avg(initLevels) >= 3 || touched(planLevels);
  const execActive = (planActive && avg(planLevels) >= 3) || touched(execLevels);

  const buckets: AmpelBuckets = { open: 0, wip: 0, done: 0, idle: 0, total: 17 };
  for (let schritt = 1; schritt <= 17; schritt++) {
    const level = lvl(schritt);
    const phaseActive = schritt <= 7 ? true : schritt <= 13 ? planActive : execActive;
    if (!phaseActive) buckets.idle += 1;
    else if (level >= 4) buckets.done += 1;
    else if (level >= 2) buckets.wip += 1;
    else buckets.open += 1;
  }
  return buckets;
}
